/**
 * FileTableView Component
 * Table view for displaying files with detailed information
 */

import React from 'react';
import { Table } from '@/components/ui/data-display';
import { FileTableRow } from '@/components/ccms-components/file-management';
import { Badge, Text } from '@/components/ui/core';
import { Checkbox } from '@/components/ui/inputs';
import { Group } from '@/components/ui/layout';
import { IconFolder } from '@tabler/icons-react';
import { FileRecordModelView, FolderInfo } from '../types';

interface FileTableViewProps {
  files: FileRecordModelView[];
  folders: FolderInfo[];
  selectedFileIds: Set<string>;
  onFileSelect: (fileId: string) => void;
  onSelectAll: () => void;
  onFileClick: (file: FileRecordModelView) => void;
  onFolderClick: (folder: FolderInfo) => void;
  onDownload: (fileId: string, fileName: string) => void;
  onPreview: (file: FileRecordModelView) => void;
  onDelete: (fileId: string) => void;
}

export const FileTableView: React.FC<FileTableViewProps> = ({
  files,
  folders,
  selectedFileIds,
  onFileSelect,
  onSelectAll,
  onFileClick,
  onFolderClick,
  onDownload,
  onPreview,
  onDelete,
}) => {
  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getFileCategory = (mimeType: string): { label: string; color: string } => {
    if (mimeType.startsWith('image/')) return { label: 'Image', color: 'green' };
    if (mimeType.startsWith('video/')) return { label: 'Video', color: 'purple' };
    if (mimeType.startsWith('audio/')) return { label: 'Audio', color: 'orange' };
    if (mimeType.includes('pdf')) return { label: 'PDF', color: 'red' };
    if (mimeType.includes('zip') || mimeType.includes('archive')) return { label: 'Archive', color: 'gray' };
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return { label: 'Spreadsheet', color: 'green' };
    if (mimeType.includes('document') || mimeType.includes('word')) return { label: 'Document', color: 'blue' };
    return { label: 'Other', color: 'gray' };
  };

  const allFilesSelected = files.length > 0 && files.every(f => selectedFileIds.has(f.id));

  return (
    <Table tableVariant="hoverable" verticalSpacing="sm">
      <thead>
        <tr>
          <th style={{ width: 40 }}>
            <Checkbox
              checked={allFilesSelected}
              indeterminate={selectedFileIds.size > 0 && !allFilesSelected}
              onChange={onSelectAll}
            />
          </th>
          <th>Name</th>
          <th>Type</th>
          <th>Size</th>
          <th>Uploaded By</th>
          <th>Uploaded At</th>
          <th>Status</th>
          <th style={{ width: 120 }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {/* Render folders */}
        {folders.map((folder) => (
          <tr
            key={`folder-${folder.id}`}
            style={{ cursor: 'pointer' }}
            onClick={() => onFolderClick(folder)}
          >
            <td></td>
            <td>
              <Group gap="xs">
                <IconFolder size={20} color={folder.color} />
                <Text fw={500}>{folder.name}</Text>
              </Group>
            </td>
            <td>
              <Badge size="sm" variant="light">Folder</Badge>
            </td>
            <td>
              <Text size="sm" c="dimmed">{folder.totalSizeFormatted}</Text>
            </td>
            <td>
              <Text size="sm" c="dimmed">-</Text>
            </td>
            <td>
              <Text size="sm" c="dimmed">{formatDate(folder.createdAt)}</Text>
            </td>
            <td>
              <Badge size="sm" variant="light">{folder.fileCount} files</Badge>
            </td>
            <td></td>
          </tr>
        ))}

        {/* Render files using FileTableRow component */}
        {files.map((file) => (
          <FileTableRow
            key={file.id}
            file={{
              id: file.id,
              fileName: file.fileName,
              mimeType: file.mimeType,
              fileSize: file.fileSize,
              uploadedBy: file.userFirstName || file.userLastName
                ? `${file.userFirstName} ${file.userLastName}`.trim()
                : file.userEmail || 'Unknown',
              uploadedAt: file.createdAt,
              status: file.status,
            }}
            selected={selectedFileIds.has(file.id)}
            onSelect={(fileId, selected) => onFileSelect(fileId)}
            onPreview={() => onPreview(file)}
            onDownload={() => onDownload(file.id, file.fileName)}
            onDelete={() => onDelete(file.id)}
            showActions
          />
        ))}
      </tbody>
    </Table>
  );
};
